import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  where,
  query,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage, FIRESTORE_ROOT } from '../firebase';
import { compressImageFile } from '../utils/imageCompression';

export type KnowledgeFileType = 'pdf' | 'docx' | 'txt' | 'markdown' | 'image' | 'audio' | 'video' | 'url' | 'other';
export type KnowledgeAssetRole = 'knowledge_source' | 'media_gallery' | 'both';
export type KnowledgeVisibility = 'internal_only' | 'usable_in_answers' | 'showable_to_user' | 'downloadable';
export type KnowledgeSourceType =
  | 'primary_source'
  | 'secondary_source'
  | 'teaching_material'
  | 'archive'
  | 'link'
  | 'visual_material'
  | 'audio_material'
  | 'video_material'
  | 'sponsor_material';
export type KnowledgeStatus = 'draft' | 'uploaded' | 'metadata_ready' | 'indexed' | 'disabled' | 'failed';
export type KnowledgeEmbeddingStatus = 'not_indexed' | 'metadata_ready' | 'indexed' | 'failed';
export type KnowledgeUsageType = 'retrieved' | 'cited' | 'shown' | 'opened' | 'downloaded' | 'ignored';

export interface KnowledgeDocument {
  id: string;
  organizationId?: number | null;
  experienceId: number;
  experienceName?: string;
  characterName?: string;
  title: string;
  description: string;
  fileType: KnowledgeFileType;
  fileUrl: string;
  storagePath?: string;
  externalUrl?: string;
  thumbnailUrl?: string;
  language: string;
  sourceType: KnowledgeSourceType;
  assetRole: KnowledgeAssetRole;
  author?: string;
  date?: string;
  rightsStatus: string;
  visibility: KnowledgeVisibility;
  retrievalEnabled: boolean;
  showToUserEnabled: boolean;
  priority: number;
  tags: string[];
  summary: string;
  mediaIntentHints: string[];
  retrievalText: string;
  status: KnowledgeStatus;
  embeddingStatus: KnowledgeEmbeddingStatus;
  transcriptionStatus?: 'not_required' | 'not_transcribed' | 'transcribed' | 'failed';
  durationSeconds?: number | null;
  active: boolean;
  createdAt?: Date | null;
  updatedAt?: Date | null;
}

export type KnowledgeDocumentInput = Omit<KnowledgeDocument, 'id' | 'createdAt' | 'updatedAt'>;

export interface KnowledgeChunk {
  id: string;
  documentId: string;
  experienceId: number;
  chunkText: string;
  chunkOrder: number;
  metadata: {
    title?: string;
    fileType?: KnowledgeFileType;
    tags?: string[];
    sourceType?: KnowledgeSourceType;
    assetRole?: KnowledgeAssetRole;
  };
  language: string;
  startTime?: number | null;
  endTime?: number | null;
  createdAt?: Date | null;
}

export type KnowledgeChunkInput = Omit<KnowledgeChunk, 'id' | 'createdAt'>;

export interface KnowledgeUsageLog {
  id: string;
  userId?: string | null;
  anonymousSessionId?: string | null;
  experienceId: number;
  documentId: string;
  conversationId?: string | null;
  usageType: KnowledgeUsageType;
  score?: number | null;
  createdAt?: Date | null;
}

export type KnowledgeUsageLogInput = Omit<KnowledgeUsageLog, 'id' | 'createdAt'>;

export interface KnowledgeRetrieveResult {
  document: KnowledgeDocument;
  chunks: KnowledgeChunk[];
  score: number;
  reason: string;
}

const documentsCollection = () => collection(db, FIRESTORE_ROOT, 'knowledge', 'documents');
const chunksCollection = () => collection(db, FIRESTORE_ROOT, 'knowledge', 'chunks');
const usageLogsCollection = () => collection(db, FIRESTORE_ROOT, 'knowledge', 'usageLogs');
const LOCAL_FALLBACK_KEY = 'glooci_knowledge_documents_v1';
const LOCAL_CHUNKS_KEY = 'glooci_knowledge_chunks_v1';
const LOCAL_USAGE_LOGS_KEY = 'glooci_knowledge_usage_logs_v1';

function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== undefined)
      .map((item) => stripUndefined(item)) as T;
  }
  if (value && typeof value === 'object' && !(value instanceof Date) && !(value instanceof Timestamp)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, item]) => item !== undefined)
        .map(([key, item]) => [key, stripUndefined(item)]),
    ) as T;
  }
  return value;
}

function isPermissionError(error: unknown): boolean {
  const maybe = error as { code?: string; message?: string };
  return maybe?.code === 'permission-denied'
    || String(maybe?.message || '').toLowerCase().includes('missing or insufficient permissions');
}

function isMissingIndexError(error: unknown): boolean {
  const maybe = error as { code?: string; message?: string };
  const message = String(maybe?.message || '').toLowerCase();
  return maybe?.code === 'failed-precondition'
    || message.includes('query requires an index')
    || message.includes('requires an index');
}

function canUseLocalStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readLocalDocuments(): KnowledgeDocument[] {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_FALLBACK_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => normalizeDocument(String(item.id || `local-${Date.now()}`), item));
  } catch {
    return [];
  }
}

function writeLocalDocuments(items: KnowledgeDocument[]): void {
  if (!canUseLocalStorage()) return;
  window.localStorage.setItem(LOCAL_FALLBACK_KEY, JSON.stringify(items));
}

function normalizeChunk(id: string, raw: Record<string, unknown>): KnowledgeChunk {
  const metadata = (raw.metadata && typeof raw.metadata === 'object' ? raw.metadata : {}) as Record<string, unknown>;
  return {
    id,
    documentId: String(raw.documentId || ''),
    experienceId: Number(raw.experienceId || 0),
    chunkText: String(raw.chunkText || ''),
    chunkOrder: Number(raw.chunkOrder || 0),
    metadata: {
      title: metadata.title ? String(metadata.title) : undefined,
      fileType: metadata.fileType ? metadata.fileType as KnowledgeFileType : undefined,
      tags: normalizeStringList(metadata.tags),
      sourceType: metadata.sourceType ? metadata.sourceType as KnowledgeSourceType : undefined,
      assetRole: metadata.assetRole ? metadata.assetRole as KnowledgeAssetRole : undefined,
    },
    language: String(raw.language || 'it'),
    startTime: raw.startTime === undefined || raw.startTime === null ? null : Number(raw.startTime),
    endTime: raw.endTime === undefined || raw.endTime === null ? null : Number(raw.endTime),
    createdAt: toDate(raw.createdAt),
  };
}

function normalizeUsageLog(id: string, raw: Record<string, unknown>): KnowledgeUsageLog {
  return {
    id,
    userId: raw.userId ? String(raw.userId) : null,
    anonymousSessionId: raw.anonymousSessionId ? String(raw.anonymousSessionId) : null,
    experienceId: Number(raw.experienceId || 0),
    documentId: String(raw.documentId || ''),
    conversationId: raw.conversationId ? String(raw.conversationId) : null,
    usageType: (raw.usageType || 'retrieved') as KnowledgeUsageType,
    score: raw.score === undefined || raw.score === null ? null : Number(raw.score),
    createdAt: toDate(raw.createdAt),
  };
}

function readLocalChunks(): KnowledgeChunk[] {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_CHUNKS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => normalizeChunk(String(item.id || `local-chunk-${Date.now()}`), item));
  } catch {
    return [];
  }
}

function writeLocalChunks(items: KnowledgeChunk[]): void {
  if (!canUseLocalStorage()) return;
  window.localStorage.setItem(LOCAL_CHUNKS_KEY, JSON.stringify(items));
}

function readLocalUsageLogs(): KnowledgeUsageLog[] {
  if (!canUseLocalStorage()) return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_USAGE_LOGS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => normalizeUsageLog(String(item.id || `local-log-${Date.now()}`), item));
  } catch {
    return [];
  }
}

function writeLocalUsageLogs(items: KnowledgeUsageLog[]): void {
  if (!canUseLocalStorage()) return;
  window.localStorage.setItem(LOCAL_USAGE_LOGS_KEY, JSON.stringify(items.slice(0, 300)));
}

function createLocalDocument(input: KnowledgeDocumentInput): KnowledgeDocument {
  const now = new Date();
  const doc = normalizeDocument(`local-${now.getTime()}-${Math.random().toString(36).slice(2, 8)}`, {
    ...input,
    retrievalText: input.retrievalText || buildRetrievalText(input),
    createdAt: now,
    updatedAt: now,
  });
  writeLocalDocuments([doc, ...readLocalDocuments()]);
  return doc;
}

function createLocalChunks(inputs: KnowledgeChunkInput[]): KnowledgeChunk[] {
  const now = new Date();
  const chunks = inputs.map((input, index) => normalizeChunk(
    `local-chunk-${now.getTime()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
    { ...input, createdAt: now },
  ));
  const existing = readLocalChunks().filter((item) => !chunks.some((chunk) => (
    chunk.documentId === item.documentId && chunk.chunkOrder === item.chunkOrder
  )));
  writeLocalChunks([...chunks, ...existing]);
  return chunks;
}

function createLocalUsageLog(input: KnowledgeUsageLogInput): KnowledgeUsageLog {
  const now = new Date();
  const log = normalizeUsageLog(`local-log-${now.getTime()}-${Math.random().toString(36).slice(2, 8)}`, {
    ...input,
    createdAt: now,
  });
  writeLocalUsageLogs([log, ...readLocalUsageLogs()]);
  return log;
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value === 'object' && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
}

function normalizeStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || '').trim())
    .filter(Boolean);
}

function normalizeDocument(id: string, raw: Record<string, unknown>): KnowledgeDocument {
  return {
    id,
    organizationId: raw.organizationId === undefined || raw.organizationId === null ? null : Number(raw.organizationId),
    experienceId: Number(raw.experienceId || 0),
    experienceName: String(raw.experienceName || ''),
    characterName: String(raw.characterName || ''),
    title: String(raw.title || ''),
    description: String(raw.description || ''),
    fileType: (raw.fileType || 'other') as KnowledgeFileType,
    fileUrl: String(raw.fileUrl || ''),
    storagePath: raw.storagePath ? String(raw.storagePath) : undefined,
    externalUrl: raw.externalUrl ? String(raw.externalUrl) : undefined,
    thumbnailUrl: raw.thumbnailUrl ? String(raw.thumbnailUrl) : undefined,
    language: String(raw.language || 'it'),
    sourceType: (raw.sourceType || 'teaching_material') as KnowledgeSourceType,
    assetRole: (raw.assetRole || 'media_gallery') as KnowledgeAssetRole,
    author: raw.author ? String(raw.author) : undefined,
    date: raw.date ? String(raw.date) : undefined,
    rightsStatus: String(raw.rightsStatus || 'da_verificare'),
    visibility: (raw.visibility || 'showable_to_user') as KnowledgeVisibility,
    retrievalEnabled: raw.retrievalEnabled !== false,
    showToUserEnabled: Boolean(raw.showToUserEnabled),
    priority: Number(raw.priority ?? 5),
    tags: normalizeStringList(raw.tags),
    summary: String(raw.summary || ''),
    mediaIntentHints: normalizeStringList(raw.mediaIntentHints),
    retrievalText: String(raw.retrievalText || ''),
    status: (raw.status || 'metadata_ready') as KnowledgeStatus,
    embeddingStatus: (raw.embeddingStatus || 'not_indexed') as KnowledgeEmbeddingStatus,
    transcriptionStatus: (raw.transcriptionStatus || 'not_required') as KnowledgeDocument['transcriptionStatus'],
    durationSeconds: raw.durationSeconds === undefined || raw.durationSeconds === null ? null : Number(raw.durationSeconds),
    active: raw.active !== false,
    createdAt: toDate(raw.createdAt),
    updatedAt: toDate(raw.updatedAt),
  };
}

export function inferKnowledgeFileType(file: File | { name?: string; type?: string }): KnowledgeFileType {
  const mime = String(file.type || '').toLowerCase();
  const name = String(file.name || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'pdf';
  if (mime.includes('word') || name.endsWith('.docx') || name.endsWith('.doc')) return 'docx';
  if (mime.includes('markdown') || name.endsWith('.md')) return 'markdown';
  if (mime.startsWith('text/') || name.endsWith('.txt')) return 'txt';
  return 'other';
}

export function defaultAssetRoleForType(fileType: KnowledgeFileType): KnowledgeAssetRole {
  if (fileType === 'image' || fileType === 'audio' || fileType === 'video' || fileType === 'url') return 'media_gallery';
  return 'knowledge_source';
}

export function defaultVisibilityForRole(role: KnowledgeAssetRole): KnowledgeVisibility {
  if (role === 'knowledge_source') return 'usable_in_answers';
  return 'showable_to_user';
}

export function buildRetrievalText(docLike: Pick<KnowledgeDocument, 'title' | 'description' | 'summary' | 'fileType' | 'tags' | 'mediaIntentHints' | 'sourceType' | 'assetRole'>): string {
  return [
    `Titolo: ${docLike.title}`,
    `Tipo: ${docLike.fileType}`,
    `Ruolo: ${docLike.assetRole}`,
    `Fonte: ${docLike.sourceType}`,
    docLike.description ? `Descrizione: ${docLike.description}` : '',
    docLike.summary ? `Abstract: ${docLike.summary}` : '',
    docLike.tags?.length ? `Tag: ${docLike.tags.join(', ')}` : '',
    docLike.mediaIntentHints?.length ? `Quando mostrarlo: ${docLike.mediaIntentHints.join('; ')}` : '',
  ].filter(Boolean).join('\n');
}

export function chunkKnowledgeText(text: string, maxLen = 1200): string[] {
  const cleaned = String(text || '')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
  if (!cleaned) return [];
  const paragraphs = cleaned
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
  const sourceParts = paragraphs.length > 0 ? paragraphs : [cleaned];
  const chunks: string[] = [];
  let current = '';

  for (const paragraph of sourceParts) {
    if (!current) {
      current = paragraph;
      continue;
    }
    if (current.length + paragraph.length + 2 <= maxLen) {
      current += `\n\n${paragraph}`;
    } else {
      chunks.push(current);
      current = paragraph;
    }
  }
  if (current) chunks.push(current);

  return chunks.flatMap((chunk) => {
    if (chunk.length <= maxLen) return [chunk];
    const fallback: string[] = [];
    for (let index = 0; index < chunk.length; index += maxLen) {
      fallback.push(chunk.slice(index, index + maxLen));
    }
    return fallback;
  });
}

async function extractPdfTextFromArrayBuffer(arrayBuffer: ArrayBuffer): Promise<string> {
  const pdfjs = await import('pdfjs-dist/build/pdf');
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.js',
    import.meta.url,
  ).toString();

  const documentTask = pdfjs.getDocument({ data: arrayBuffer });
  const pdfDocument = await documentTask.promise;
  const pages: string[] = [];

  for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
    const page = await pdfDocument.getPage(pageNumber);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item: { str?: string } | Record<string, unknown>) => (
        item && typeof item === 'object' && 'str' in item
          ? String((item as { str?: unknown }).str || '')
          : ''
      ))
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (pageText) pages.push(pageText);
  }

  return pages.join('\n\n').trim();
}

export async function extractKnowledgeTextFromFile(file: File): Promise<string> {
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  if (name.endsWith('.pdf') || type.includes('pdf')) {
    return extractPdfTextFromArrayBuffer(await file.arrayBuffer());
  }
  if (name.endsWith('.docx') || type.includes('wordprocessingml')) {
    const mammoth = await import('mammoth/mammoth.browser');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return String(result.value || '').trim();
  }
  if (
    name.endsWith('.txt')
    || name.endsWith('.md')
    || name.endsWith('.csv')
    || name.endsWith('.json')
    || name.endsWith('.html')
    || type.startsWith('text/')
    || type.includes('json')
  ) {
    return (await file.text()).trim();
  }
  return '';
}

export async function extractKnowledgeTextFromDocument(document: Pick<KnowledgeDocument, 'fileType' | 'fileUrl' | 'externalUrl'>): Promise<string> {
  const sourceUrl = String(document.fileUrl || document.externalUrl || '').trim();
  if (!sourceUrl) return '';

  const response = await fetch(sourceUrl);
  if (!response.ok) {
    throw new Error(`Download materiale non riuscito: HTTP ${response.status}`);
  }

  if (document.fileType === 'pdf') {
    return extractPdfTextFromArrayBuffer(await response.arrayBuffer());
  }
  if (document.fileType === 'txt' || document.fileType === 'markdown') {
    return (await response.text()).trim();
  }
  if (document.fileType === 'docx') {
    const mammoth = await import('mammoth/mammoth.browser');
    const result = await mammoth.extractRawText({ arrayBuffer: await response.arrayBuffer() });
    return String(result.value || '').trim();
  }

  return '';
}

export async function listKnowledgeDocumentsForExperience(experienceId: number): Promise<KnowledgeDocument[]> {
  if (!Number.isFinite(experienceId) || experienceId <= 0) return [];
  try {
    const q = query(documentsCollection(), where('experienceId', '==', Number(experienceId)));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((item) => normalizeDocument(item.id, item.data()))
      .filter((item) => item.active)
      .sort((a, b) => {
        const aTime = a.updatedAt?.getTime() || a.createdAt?.getTime() || 0;
        const bTime = b.updatedAt?.getTime() || b.createdAt?.getTime() || 0;
        return bTime - aTime;
      });
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    return readLocalDocuments()
      .filter((item) => item.active && Number(item.experienceId) === Number(experienceId))
      .sort((a, b) => {
        const aTime = a.updatedAt?.getTime() || a.createdAt?.getTime() || 0;
        const bTime = b.updatedAt?.getTime() || b.createdAt?.getTime() || 0;
        return bTime - aTime;
      });
  }
}

export async function uploadKnowledgeMediaFile(params: {
  file: File;
  experienceId: number;
}): Promise<{ fileUrl: string; storagePath: string }> {
  const uploadFileData = await compressImageFile(params.file, {
    maxWidth: 1600,
    maxHeight: 1600,
    maxBytes: 750 * 1024,
  });
  const safeName = String(uploadFileData.name || 'asset')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 120) || 'asset';
  const objectPath = `${FIRESTORE_ROOT}/knowledge/experiences/${params.experienceId}/${Date.now()}-${safeName}`;
  const storageRef = ref(storage, objectPath);
  await uploadBytes(storageRef, uploadFileData);
  const fileUrl = await getDownloadURL(storageRef);
  return { fileUrl, storagePath: objectPath };
}

export async function listKnowledgeChunksForDocument(documentId: string): Promise<KnowledgeChunk[]> {
  if (!documentId) return [];
  try {
    const q = query(chunksCollection(), where('documentId', '==', documentId), orderBy('chunkOrder', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((item) => normalizeChunk(item.id, item.data()));
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    return readLocalChunks()
      .filter((item) => item.documentId === documentId)
      .sort((a, b) => a.chunkOrder - b.chunkOrder);
  }
}

export async function listKnowledgeChunksForExperience(experienceId: number): Promise<KnowledgeChunk[]> {
  if (!Number.isFinite(experienceId) || experienceId <= 0) return [];
  try {
    const q = query(chunksCollection(), where('experienceId', '==', Number(experienceId)));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((item) => normalizeChunk(item.id, item.data()))
      .sort((a, b) => a.chunkOrder - b.chunkOrder);
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    return readLocalChunks()
      .filter((item) => Number(item.experienceId) === Number(experienceId))
      .sort((a, b) => a.chunkOrder - b.chunkOrder);
  }
}

export async function createKnowledgeChunks(inputs: KnowledgeChunkInput[]): Promise<KnowledgeChunk[]> {
  if (inputs.length === 0) return [];
  try {
    const created: KnowledgeChunk[] = [];
    for (const input of inputs) {
      const docRef = await addDoc(chunksCollection(), stripUndefined({
        ...input,
        createdAt: serverTimestamp(),
      }));
      created.push(normalizeChunk(docRef.id, { ...input, createdAt: new Date() }));
    }
    return created;
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    return createLocalChunks(inputs);
  }
}

export async function indexKnowledgeDocumentFromText(
  document: KnowledgeDocument,
  text: string,
): Promise<KnowledgeChunk[]> {
  const sourceText = String(text || document.summary || document.description || document.retrievalText || '').trim();
  const chunks = chunkKnowledgeText(sourceText);
  const inputs: KnowledgeChunkInput[] = chunks.map((chunkText, index) => ({
    documentId: document.id,
    experienceId: document.experienceId,
    chunkText,
    chunkOrder: index,
    metadata: {
      title: document.title,
      fileType: document.fileType,
      tags: document.tags,
      sourceType: document.sourceType,
      assetRole: document.assetRole,
    },
    language: document.language || 'it',
    startTime: null,
    endTime: null,
  }));
  const created = await createKnowledgeChunks(inputs);
  await updateKnowledgeDocument(document.id, {
    ...document,
    status: created.length > 0 ? 'indexed' : 'metadata_ready',
    embeddingStatus: created.length > 0 ? 'indexed' : 'metadata_ready',
  });
  return created;
}

export async function createKnowledgeDocument(input: KnowledgeDocumentInput): Promise<KnowledgeDocument> {
  const docInput = {
    ...input,
    retrievalText: input.retrievalText || buildRetrievalText(input),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  try {
    const refDoc = await addDoc(documentsCollection(), stripUndefined(docInput));
    return normalizeDocument(refDoc.id, {
      ...docInput,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    return createLocalDocument(input);
  }
}

export async function updateKnowledgeDocument(
  id: string,
  patch: Partial<KnowledgeDocumentInput>,
): Promise<void> {
  const nextPatch: Partial<KnowledgeDocumentInput> & { updatedAt: ReturnType<typeof serverTimestamp> } = {
    ...patch,
    updatedAt: serverTimestamp(),
  };
  if (
    patch.title !== undefined
    || patch.description !== undefined
    || patch.summary !== undefined
    || patch.tags !== undefined
    || patch.mediaIntentHints !== undefined
    || patch.fileType !== undefined
    || patch.sourceType !== undefined
    || patch.assetRole !== undefined
  ) {
    nextPatch.retrievalText = buildRetrievalText({
      title: String(patch.title || ''),
      description: String(patch.description || ''),
      summary: String(patch.summary || ''),
      fileType: (patch.fileType || 'other') as KnowledgeFileType,
      tags: patch.tags || [],
      mediaIntentHints: patch.mediaIntentHints || [],
      sourceType: (patch.sourceType || 'teaching_material') as KnowledgeSourceType,
      assetRole: (patch.assetRole || 'media_gallery') as KnowledgeAssetRole,
    });
  }
  try {
    await updateDoc(doc(db, FIRESTORE_ROOT, 'knowledge', 'documents', id), stripUndefined(nextPatch));
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    const updatedAt = new Date();
    const items = readLocalDocuments().map((item) => (
      item.id === id
        ? normalizeDocument(id, { ...item, ...patch, updatedAt })
        : item
    ));
    writeLocalDocuments(items);
  }
}

export async function softDisableKnowledgeDocument(id: string): Promise<void> {
  try {
    await updateDoc(doc(db, FIRESTORE_ROOT, 'knowledge', 'documents', id), {
      active: false,
      status: 'disabled',
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    writeLocalDocuments(readLocalDocuments().map((item) => (
      item.id === id
        ? { ...item, active: false, status: 'disabled', updatedAt: new Date() }
        : item
    )));
  }
}

export async function hardDeleteKnowledgeDocument(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, FIRESTORE_ROOT, 'knowledge', 'documents', id));
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    writeLocalDocuments(readLocalDocuments().filter((item) => item.id !== id));
  }
}

function tokenize(value: string): string[] {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);
}

function scoreText(queryTokens: string[], text: string): number {
  if (queryTokens.length === 0) return 0;
  const haystack = ` ${tokenize(text).join(' ')} `;
  return queryTokens.reduce((score, token) => {
    if (haystack.includes(` ${token} `)) return score + 2;
    if (haystack.includes(token)) return score + 1;
    return score;
  }, 0);
}

export async function retrieveKnowledgeForExperience(
  experienceId: number,
  searchText: string,
  options: { includeMedia?: boolean; maxDocuments?: number } = {},
): Promise<KnowledgeRetrieveResult[]> {
  const queryTokens = tokenize(searchText);
  const [documents, chunks] = await Promise.all([
    listKnowledgeDocumentsForExperience(experienceId),
    listKnowledgeChunksForExperience(experienceId),
  ]);
  const byDocument = new Map<string, KnowledgeChunk[]>();
  chunks.forEach((chunk) => {
    const list = byDocument.get(chunk.documentId) || [];
    list.push(chunk);
    byDocument.set(chunk.documentId, list);
  });

  const results = documents
    .filter((document) => document.retrievalEnabled || (options.includeMedia && document.showToUserEnabled))
    .map((document) => {
      const docChunks = byDocument.get(document.id) || [];
      const textScore = scoreText(queryTokens, [
        document.title,
        document.description,
        document.summary,
        document.tags.join(' '),
        document.mediaIntentHints.join(' '),
        document.retrievalText,
      ].join('\n'));
      const chunkScores = docChunks.map((chunk) => scoreText(queryTokens, chunk.chunkText));
      const bestChunkScore = chunkScores.length ? Math.max(...chunkScores) : 0;
      const showableBonus = document.showToUserEnabled && options.includeMedia ? 1 : 0;
      const priorityBonus = Math.max(0, 11 - Number(document.priority || 5)) / 10;
      const score = textScore + bestChunkScore + showableBonus + priorityBonus;
      return {
        document,
        chunks: docChunks
          .map((chunk, index) => ({ chunk, score: chunkScores[index] || 0 }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 3)
          .map((item) => item.chunk),
        score,
        reason: score > 0 ? 'corrispondenza su metadati, tag o testo ricercabile' : 'nessuna corrispondenza forte',
      };
    })
    .filter((result) => result.score > 0 || queryTokens.length === 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, options.maxDocuments || 5);

  if (searchText.trim() && results.length > 0) {
    await logKnowledgeUsage({
      experienceId,
      documentId: results[0].document.id,
      usageType: 'retrieved',
      score: results[0].score,
    });
  }

  return results;
}

export async function logKnowledgeUsage(input: KnowledgeUsageLogInput): Promise<KnowledgeUsageLog> {
  try {
    const docRef = await addDoc(usageLogsCollection(), stripUndefined({
      experienceId: input.experienceId,
      documentId: input.documentId,
      usageType: input.usageType,
      score: input.score,
      createdAt: serverTimestamp(),
    }));
    return normalizeUsageLog(docRef.id, {
      experienceId: input.experienceId,
      documentId: input.documentId,
      usageType: input.usageType,
      score: input.score,
      createdAt: new Date(),
    });
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    return createLocalUsageLog(input);
  }
}

export async function listKnowledgeUsageLogsForExperience(experienceId: number): Promise<KnowledgeUsageLog[]> {
  if (!Number.isFinite(experienceId) || experienceId <= 0) return [];
  try {
    try {
      const q = query(
        usageLogsCollection(),
        where('experienceId', '==', Number(experienceId)),
        orderBy('createdAt', 'desc'),
        limit(50),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((item) => normalizeUsageLog(item.id, item.data()));
    } catch (error) {
      if (!isMissingIndexError(error)) throw error;
      const q = query(
        usageLogsCollection(),
        where('experienceId', '==', Number(experienceId)),
        limit(100),
      );
      const snapshot = await getDocs(q);
      return snapshot.docs
        .map((item) => normalizeUsageLog(item.id, item.data()))
        .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0))
        .slice(0, 50);
    }
  } catch (error) {
    if (!isPermissionError(error)) throw error;
    return readLocalUsageLogs()
      .filter((item) => Number(item.experienceId) === Number(experienceId))
      .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0))
      .slice(0, 50);
  }
}
